import React, { useState, useEffect } from 'react';
import api from '../../../api';
import { toast } from 'react-toastify';

const StatusButton = ({ student, onStatusChange, size = 'md', showLabel = true, variant = 'default' }) => {
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  // Debug: Log student object on mount and when it changes
  useEffect(() => {
    console.log('🔍 [StatusButton] Student object received:', {
      id: student?._id,
      studentId: student?.studentId,
      tenantId: student?.tenantId,
      status: student?.status,
      fullObject: student
    });
  }, [student]);

  const handleToggle = () => {
    const newStatus = student.status === 'active' ? 'inactive' : 'active';
    setPendingStatus(newStatus);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setShowConfirm(false);
    
    try {
      const token = localStorage.getItem('token');
      const studentId = student.studentId || student._id;
      const tenantId = student.tenantId;
      
      // Debug logs
      console.log('📤 [StatusButton] Sending request:', {
        url: `/api/students/${studentId}/status`,
        payload: { 
          status: pendingStatus,
          tenantId: tenantId 
        },
        hasToken: !!token
      });

      if (!tenantId) {
        console.error('❌ [StatusButton] tenantId is missing!');
        toast.error('Tenant ID is missing. Cannot update status.');
        setLoading(false);
        setPendingStatus(null);
        return;
      }

      const response = await api.put(
        `/api/students/${studentId}/status`,
        { 
          status: pendingStatus,
          tenantId: tenantId 
        },
        { 
          headers: { 
            Authorization: `Bearer ${token}` 
          } 
        }
      );
      
      console.log('✅ [StatusButton] Update successful:', response.data);
      
      if (onStatusChange) {
        onStatusChange(studentId, pendingStatus);
      }
      toast.success(`Student ${pendingStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('❌ [StatusButton] Update failed:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      
      // Show specific error message
      if (error.response?.status === 400) {
        toast.error(error.response.data.message || 'Bad request');
      } else if (error.response?.status === 404) {
        toast.error('Student not found');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized - please login again');
      } else {
        toast.error(error.response?.data?.message || 'Failed to update student status');
      }
    } finally {
      setLoading(false);
      setPendingStatus(null);
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setPendingStatus(null);
  };

  // Size configurations
  const sizeConfig = {
    sm: {
      badge: 'px-2 py-1 text-xs',
      switch: 'w-10 h-5',
      knob: 'w-3 h-3',
      knobTransform: 'translate-x-5',
      label: 'text-xs',
      spinner: 'h-3 w-3'
    },
    md: {
      badge: 'px-3 py-1.5 text-sm',
      switch: 'w-12 h-6',
      knob: 'w-4 h-4',
      knobTransform: 'translate-x-6',
      label: 'text-sm',
      spinner: 'h-4 w-4'
    },
    lg: {
      badge: 'px-4 py-2 text-base',
      switch: 'w-14 h-7',
      knob: 'w-5 h-5',
      knobTransform: 'translate-x-7',
      label: 'text-base',
      spinner: 'h-5 w-5'
    }
  };

  // Variant configurations
  const variantConfig = {
    default: {
      active: {
        badge: 'bg-green-50 text-green-700 border border-green-200 shadow-sm',
        switch: 'bg-green-500',
        label: 'text-green-600'
      },
      inactive: {
        badge: 'bg-gray-50 text-gray-700 border border-gray-200 shadow-sm',
        switch: 'bg-gray-400',
        label: 'text-gray-600'
      }
    },
    minimal: {
      active: {
        badge: 'bg-green-100 text-green-800',
        switch: 'bg-green-500',
        label: 'text-green-700'
      },
      inactive: {
        badge: 'bg-gray-200 text-gray-600',
        switch: 'bg-gray-400',
        label: 'text-gray-500'
      }
    },
    outlined: {
      active: {
        badge: 'border-2 border-green-500 text-green-700 bg-white',
        switch: 'bg-green-500',
        label: 'text-green-700'
      },
      inactive: {
        badge: 'border-2 border-gray-400 text-gray-600 bg-white',
        switch: 'bg-gray-400',
        label: 'text-gray-600'
      }
    }
  };

  const config = sizeConfig[size];
  const variantStyle = variantConfig[variant];
  const currentStatus = student.status === 'active';
  const statusStyle = currentStatus ? variantStyle.active : variantStyle.inactive;

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <span
              className={`${config.badge} ${statusStyle.badge} rounded-full font-semibold flex items-center gap-2 transition-all duration-200`}
              title={currentStatus ? 'Student is currently active' : 'Student is currently inactive'}
            >
              <span
                className={`w-2 h-2 rounded-full animate-pulse ${
                  currentStatus ? 'bg-green-500' : 'bg-gray-500'
                }`}
              />
              {currentStatus ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className={`flex items-center cursor-pointer group ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}>
            <div className="relative">
              <input
                type="checkbox"
                checked={currentStatus}
                disabled={loading}
                onChange={handleToggle}
                className="sr-only"
              />
              <div
                className={`${config.switch} flex items-center rounded-full p-1 transition-all duration-300 ${
                  statusStyle.switch
                } ${loading ? 'opacity-50' : 'group-hover:shadow-md'}`}
              >
                <div
                  className={`${config.knob} bg-white rounded-full shadow-lg transform transition-all duration-300 ${
                    currentStatus ? config.knobTransform : ''
                  } ${loading ? 'animate-pulse' : ''}`}
                />
              </div>
            </div>
            {showLabel && (
              <span className={`ml-3 ${config.label} font-medium ${statusStyle.label} transition-colors duration-200`}>
                {currentStatus ? 'Deactivate' : 'Activate'}
              </span>
            )}
          </label>

          {loading && (
            <div className="flex items-center">
              <svg className={`animate-spin ${config.spinner} text-gray-400`} viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm">
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto transform transition-all duration-300 scale-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-yellow-100 to-yellow-50">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-800 text-center mb-2">
                {pendingStatus === 'active' ? 'Activate Student' : 'Deactivate Student'}
              </h2>
              <p className="text-gray-600 text-center text-sm leading-relaxed">
                You are about to {pendingStatus === 'active' ? 'activate' : 'deactivate'}{' '}
                <span className="font-semibold text-gray-800">{student.firstName} {student.lastName}</span>.
                {pendingStatus === 'inactive' && (
                  <span className="block mt-1 text-yellow-600 font-medium">
                    This will restrict their access to the system.
                  </span>
                )}
              </p>
            </div>

            <div className="p-4 bg-gray-50 border-y border-gray-100">
              <div className="flex items-center space-x-3">
                {student.profilePhotoUrl ? (
                  <img
                    className="h-10 w-10 rounded-full object-cover border-2 border-white shadow-sm"
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/student-profile/${student.studentId}?t=${Date.now()}`}
                    alt=""
                    onError={e => {
                      e.target.onerror = null;
                      e.target.src = "";
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-2 border-white shadow-sm">
                    <span className="text-white font-bold text-sm">
                      {student.firstName?.charAt(0)}{student.lastName?.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {student.firstName} {student.lastName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {student.admissionNumber || 'No admission number'} • {student.className || 'No class'}
                  </p>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  pendingStatus === 'active' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-gray-100 text-gray-800 border border-gray-200'
                }`}>
                  {pendingStatus === 'active' ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 p-6">
              <button
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                className={`flex-1 px-6 py-3 border-2 rounded-xl text-sm font-semibold text-white transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${
                  pendingStatus === 'active'
                    ? 'bg-gradient-to-r from-green-500 to-green-600 border-green-600 hover:from-green-600 hover:to-green-700'
                    : 'bg-gradient-to-r from-gray-500 to-gray-600 border-gray-600 hover:from-gray-600 hover:to-gray-700'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </span>
                ) : pendingStatus === 'active' ? (
                  <span className="flex items-center justify-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Activate Student
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Deactivate Student
                  </span>
                )}
              </button>
            </div>

            <div className="px-6 py-3 bg-gray-50 rounded-b-2xl border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center">
                This action can be reversed at any time
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

StatusButton.defaultProps = {
  size: 'md',
  showLabel: true,
  variant: 'default'
};

export default StatusButton;